// globalStyles.js
import { createGlobalStyle } from "styled-components/macro";
import { device, toRem } from "./Breakpoints";
import { noSelect } from "theme/GlobalElements";
import { colors } from "theme/Theme";

const ratios = {
  horizontalScaleRatio: 1.125,
  verticalScaleRatio: 1.25,
  baseFontSize: 16,
};

const typeScaleVertical = {
  textXS:
    ratios.baseFontSize /
    (ratios.verticalScaleRatio * ratios.verticalScaleRatio),
  textSM: ratios.baseFontSize / ratios.verticalScaleRatio,
  textBody: ratios.baseFontSize,
  textMD: ratios.baseFontSize * ratios.verticalScaleRatio,
  textLG:
    ratios.baseFontSize *
    (ratios.verticalScaleRatio * ratios.verticalScaleRatio),
  textXL:
    ratios.baseFontSize *
    (ratios.verticalScaleRatio *
      ratios.verticalScaleRatio *
      ratios.verticalScaleRatio),
  textXXL:
    ratios.baseFontSize *
    (ratios.verticalScaleRatio *
      ratios.verticalScaleRatio *
      ratios.verticalScaleRatio *
      ratios.verticalScaleRatio),
  textXXXL:
    ratios.baseFontSize *
    (ratios.verticalScaleRatio *
      ratios.verticalScaleRatio *
      ratios.verticalScaleRatio *
      ratios.verticalScaleRatio *
      ratios.verticalScaleRatio),
};

enum TextTypeEnum {
  textXS, // mobile: 16 / (1.25 * 1.25), laptop: 10.24 / (1.125 * 1.125)
  textSM, // 16 / 1.25
  textBody, //16
  textMD, // 16 * 1.25
  textLG, // * 1.25 * 1.25
  textXL,
  textXXL,
  textXXXL,
}

const typeScaleHorizontal = {
  [TextTypeEnum.textXS]: {
    mobile: typeScaleVertical.textXS,
    laptop: typeScaleVertical.textXS * ratios.horizontalScaleRatio,
    desktop:
      typeScaleVertical.textXS *
      (ratios.horizontalScaleRatio * ratios.horizontalScaleRatio),
  },
  [TextTypeEnum.textSM]: {
    mobile: typeScaleVertical.textSM,
    laptop: typeScaleVertical.textSM * ratios.horizontalScaleRatio,
    desktop:
      typeScaleVertical.textSM *
      (ratios.horizontalScaleRatio * ratios.horizontalScaleRatio),
  },
  [TextTypeEnum.textBody]: {
    mobile: typeScaleVertical.textBody,
    laptop: typeScaleVertical.textBody,
    desktop: typeScaleVertical.textBody * ratios.horizontalScaleRatio,
  },
  [TextTypeEnum.textMD]: {
    mobile: typeScaleVertical.textMD,
    laptop: typeScaleVertical.textMD * ratios.horizontalScaleRatio,
    desktop:
      typeScaleVertical.textMD *
      (ratios.horizontalScaleRatio * ratios.horizontalScaleRatio),
  },
  [TextTypeEnum.textLG]: {
    mobile: typeScaleVertical.textLG,
    laptop: typeScaleVertical.textLG * ratios.horizontalScaleRatio,
    desktop: typeScaleVertical.textLG * ratios.horizontalScaleRatio,
  },
  [TextTypeEnum.textXL]: {
    mobile: typeScaleVertical.textXL,
    laptop: typeScaleVertical.textXL * ratios.horizontalScaleRatio,
    desktop:
      typeScaleVertical.textXL *
      (ratios.horizontalScaleRatio * ratios.horizontalScaleRatio),
  },
  [TextTypeEnum.textXXL]: {
    mobile: typeScaleVertical.textXXL,
    laptop: typeScaleVertical.textXXL * ratios.horizontalScaleRatio,
    desktop:
      typeScaleVertical.textXXL *
      (ratios.horizontalScaleRatio * ratios.horizontalScaleRatio),
  },
  [TextTypeEnum.textXXXL]: {
    mobile: typeScaleVertical.textXXXL,
    laptop: typeScaleVertical.textXXXL * ratios.horizontalScaleRatio,
    desktop:
      typeScaleVertical.textXXXL *
      (ratios.horizontalScaleRatio * ratios.horizontalScaleRatio),
  },
};

interface MoreStylesObject {
  mobile: string;
  laptop: string;
  desktop: string;
}

const getStyleForText = (
  propName: TextTypeEnum,
  isHeading: boolean = false,
  stylesObj?: MoreStylesObject
) => {
  return `
    font-size: ${typeScaleHorizontal[propName].mobile}px;
    line-height: 1.5;
    ${
      isHeading
        ? `margin-bottom: ${
            typeScaleHorizontal[propName].mobile / ratios.verticalScaleRatio
          }px`
        : ""
    };
    ${stylesObj?.mobile ?? ""}
    
    @media only screen and (${device.laptop}) {
      font-size: ${typeScaleHorizontal[propName].laptop}px;
    ${
      isHeading
        ? `margin-bottom: ${
            typeScaleHorizontal[propName].laptop / ratios.verticalScaleRatio
          }px`
        : ""
    };
      ${stylesObj?.laptop ?? ""}
    }

    @media only screen and (${device.desktopL}) {
      font-size: ${typeScaleHorizontal[propName].desktop}px;
    ${
      isHeading
        ? `margin-bottom: ${
            typeScaleHorizontal[propName].desktop / ratios.verticalScaleRatio
          }px`
        : ""
    };
      ${stylesObj?.desktop ?? ""}
    }
  `;
};

const GlobalStyle = createGlobalStyle`
  .ReactModal__Content {
    min-width: 60%;

    @media only screen and (${device.laptop}) {
      min-width: 0px;
    }
  }

  body,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  p,
  span,
  button,
  input,
  select,
  textarea {
    margin: 0;
    padding: 0;
  }
  ::-webkit-input-placeholder {
  }
  :-moz-placeholder {
  }
  ::-moz-placeholder {
  }
  :-ms-input-placeholder {
  }
  .no-scroll {
    /* position: fixed;  */
    overflow-y: hidden;
    width: inherit;
    @media only screen and (${device.laptop}) { 
      padding-right: 8px;
    }
  }
  
  button {
    background: transparent;
    box-shadow: 0px 0px 0px transparent;
    border: 0px solid transparent;
    text-shadow: 0px 0px 0px transparent;
    font-family: "Barlow", sans-serif;
  }

  button:hover {
    background: transparent;
    box-shadow: 0px 0px 0px transparent;
    border: 0px solid transparent;
    text-shadow: 0px 0px 0px transparent;
  }

  button:active {
    outline: none;
    border: none;
  }

  button:focus {
    outline: 0;
  }

  .shadow {
    box-shadow: 0 1px 1px rgba(0, 0, 0, 0.11), 0 2px 2px rgba(0, 0, 0, 0.11),
      0 4px 4px rgba(0, 0, 0, 0.11), 0 6px 8px rgba(0, 0, 0, 0.11),
      0 8px 16px rgba(0, 0, 0, 0.11);
  }

  .small-shadow {
    box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05), 0 2px 2px rgba(0, 0, 0, 0.05),
      0 3px 3px rgba(0, 0, 0, 0.05), 0 4px 6px rgba(0, 0, 0, 0.05),
      0 6px 8px rgba(0, 0, 0, 0.05);
  }

  img {
    ${noSelect};
  }
  p {
    margin: 0;
    padding: 0;

    &.title {
      font-size: ${toRem(25)};
      line-height: ${toRem(25 * 1.4)};
    }

    &.subTitle {
      font-size: ${toRem(14)};
      line-height: ${toRem(14 * 1.4)};
    }

    &.content {
      font-size: ${toRem(16)};
      line-height: ${toRem(16 * 1.4)};
    }

    @media only screen and (${device.laptop}) {
      &.title {
        font-size: ${toRem(31)};
        line-height: ${toRem(31 * 1.4)};
      }

      &.subTitle {
        font-size: ${toRem(16)};
        line-height: ${toRem(16 * 1.4)};
      }

      &.content {
        font-size: ${toRem(18)};
        line-height: ${toRem(18 * 1.4)};
      }
    }
  }

  //TYPOGRAPHY
  .text-xs {
    ${getStyleForText(TextTypeEnum.textXS)}
  }

  .text-sm {
    ${getStyleForText(TextTypeEnum.textSM)}
  }

  .text-body {
    ${getStyleForText(TextTypeEnum.textBody)}
  }

  .text-md {
    ${getStyleForText(TextTypeEnum.textMD)}
  }

  .text-lg {
    ${getStyleForText(TextTypeEnum.textLG)}
  }

  .text-xl {
    ${getStyleForText(TextTypeEnum.textXL)}
  }

  .text-xxl {
    ${getStyleForText(TextTypeEnum.textXXL)}
  }

  .text-xxxl {
    ${getStyleForText(TextTypeEnum.textXXXL)}
  }

  h1 {
    ${getStyleForText(TextTypeEnum.textXXL, true)}
  }

  h2 {
    ${getStyleForText(TextTypeEnum.textXL, true)}
  }


  h3 {
    ${getStyleForText(TextTypeEnum.textLG, true)}
  }

  h4 {
    ${getStyleForText(TextTypeEnum.textMD, true)}
  }

  .caption {
    ${getStyleForText(TextTypeEnum.textSM)};
    color: ${colors.grayest};
    font-weight: 400;
  }

  h1,h2,h3,h4 {
    color: ${colors.light_blue}
  }

  ::-webkit-scrollbar {
  width: 5px;
}

/* Track */
::-webkit-scrollbar-track {
  background: ${colors.gray}; 
}

/* Handle */
::-webkit-scrollbar-thumb {
  background: ${colors.dark_gray}; 
}

/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
  background: ${colors.light_blue};
}
`;

export default GlobalStyle;
